import React, { FC, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useSearchClientSavedFiltersMutation } from "../../../../api/services/client/clientApis";
import {
  MenuListParams,
  SaveSearchFilterParams,
} from "../../../../api/services/client/models";
import { useGetCoachAssignmentFunction } from "../../../../api/services/program/programApis";
import { ReactComponent as DeleteIcon } from "../../../../assets/images/delete-icon.svg";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiMenuList,
  OptionsDataType,
  SearchSaveFiltersSubHeader
} from "../../../../components";
import { CommonListModal } from "../../../../components/commonListModal/CommonListModal";
import { DeleteFilterModal } from "../../../../components/searchFilters/deleteFilter/DeleteFilter";
import { DeleteMultipleFilterModal } from "../../../../components/searchFilters/deleteMultipleFilters/DeleteMultipleFilters";
import { OverwriteFilterModal } from "../../../../components/searchFilters/overwriteFilter/OverwriteFilter";
import { SaveFilterModal } from "../../../../components/searchFilters/saveFilter/SaveFilter";
import {
  MAX_SAVE_FILTERS_LIMIT,
  PAGE_IDS,
  SAVED_SEARCH_PAGE_NAME
} from "../../../../helpers/constants";
import {
  isEmpty,
  isMyCaseLoads,
  isNotEmpty,
  mapFiltersToFormValues,
  trimValue
} from "../../../../helpers/utils";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { FilterProps } from "../../../clientSearch/modelsAndSchemas/Models";
import { convertCoachDataToDropdown } from "../../../createProgram/helper";
import {
  ParticipantsSearchFormKey,
  ParticipantsSearchFormProps,
  SEARCH_TYPE_KEY,
} from "../../modelsAndSchemas/Models";
import { ParticipantsSearchForm } from "../components/participantsSearchForm/ParticipantsSearchForm";
import { participantsSearchDefaultValues } from "../ParticipantsMain";
import { ParticipantsSearchWrapper } from "./ParticipantsSearch.style";

interface ParticipantsSearchProps {
  coachesList: OptionsDataType[];
  setIsSearchClick: (value: boolean) => void;
  makeMobileFieldFocussed?: boolean;
}
const pageId = PAGE_IDS.SEARCH_PARTICIPANTS;
export const ParticipantsSearch: FC<ParticipantsSearchProps> = ({
  coachesList,
  setIsSearchClick,
  makeMobileFieldFocussed = false,
}) => {
  const { commonLabels } = useSiteCore();
  const { isMobile } = useMediaQueries();
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [showParticipantSavedFilter, setShowParticipantSavedFilter] =
    useState(false);
  const [showDeleteMultipleFilterModal, setShowDeleteMultipleFilterModal] =
    useState(false);
  const [showDeleteFilterModal, setShowDeleteFilterModal] = useState(false);
  const [filterToDeleteParticipant, setFilterToDeleteParticipant] =
    useState<MenuListParams>({
      id: "",
      name: "",
    });
  const [participantFiltersData, setParticipantFiltersData] = useState<
    FilterProps[]
  >([]);
  const [
    isOpenParticipantSaveFilterModal,
    setIsOpenParticipantSaveFilterModal,
  ] = useState(false);
  const [preloadParticipantFilter, setPreloadParticipantFilter] =
    useState(false);
  const [isMaxFilterReached, setIsMaxFilterReached] = useState(false);
  const [preloadParticipantFilterValues, setPreloadParticipantFilterValues] =
    useState({
      id: "",
      name: "",
      filters: participantFiltersData,
    });
  const [finalFiltersList, setFinalFiltersList] = useState<
    SaveSearchFilterParams[]
  >([]);

  const [selectedSavedSearchPageName, setSelectedSavedSearchPageName] =
    useState(SAVED_SEARCH_PAGE_NAME.COACH_PORTAL_MY_CASE_LOADS_SEARCH);

  const { reset, getValues } = useFormContext();

  const { searchType } = getValues();

  const isSearchTypeMyCaseLoads = useMemo(
    () => isMyCaseLoads(searchType),
    [searchType],
  );
  const { data: coachAssignmentData } = useGetCoachAssignmentFunction();

  const {
    data: listOfFiltersMyCaseLoads,
    refetch: refetchListOfFiltersMyCaseLoads,
    isFetching: isListOfFiltersMyCaseLoadsFetching,
  } = useSearchClientSavedFiltersMutation({
    searchPageName: "CoachPortalMyCaseLoadsSearch",
  });

  const {
    data: listOfFiltersAllParticipants,
    refetch: refetchListOfFiltersAllParticipants,
    isFetching: isListOfFiltersAllParticipantsFetching,
  } = useSearchClientSavedFiltersMutation({
    searchPageName: "CoachPortalAllParticipantsSearch",
  });

  useEffect(() => {
    if (isSearchTypeMyCaseLoads) {
      setFinalFiltersList([]);
      setFinalFiltersList([...(listOfFiltersMyCaseLoads || [])]);
    }
  }, [isListOfFiltersMyCaseLoadsFetching, isSearchTypeMyCaseLoads]);

  useEffect(() => {
    if (!isSearchTypeMyCaseLoads) {
      setFinalFiltersList([]);
      setFinalFiltersList([...(listOfFiltersAllParticipants || [])]);
    }
  }, [isListOfFiltersAllParticipantsFetching, isSearchTypeMyCaseLoads]);

  const removeSavedFilters = () => {
    setPreloadParticipantFilterValues({ id: "", name: "", filters: [] });
    setPreloadParticipantFilter(false);
  };

  const resetFormValueBySearchType = (isRadioToggleChange: boolean) => {
    const { resetForm, searchType } = getValues();
    reset({
      ...participantsSearchDefaultValues,
      [ParticipantsSearchFormKey.resetForm]: !resetForm,
      ...(isRadioToggleChange && {
        [ParticipantsSearchFormKey.searchType]: searchType,
      }),
    });

    const isSearchTypeMyCaseLoadsRecent =
      searchType === SEARCH_TYPE_KEY.myCaseLoads;

    if (!isRadioToggleChange || isSearchTypeMyCaseLoadsRecent) {
      refetchListOfFiltersMyCaseLoads();
      setSelectedSavedSearchPageName(
        SAVED_SEARCH_PAGE_NAME.COACH_PORTAL_MY_CASE_LOADS_SEARCH,
      );
    } else {
      refetchListOfFiltersAllParticipants();
      setSelectedSavedSearchPageName(
        SAVED_SEARCH_PAGE_NAME.COACH_PORTAL_ALL_PARTICIPANTS_SEARCH,
      );
    }

    removeSavedFilters();
    setIsSearchClick(false);
  };

  const handleSetParticipantFilterModal = () => {
    setIsOpenParticipantSaveFilterModal(!isOpenParticipantSaveFilterModal);
  };

  const handleParticipantSaveFilterModal = () => {
    if (
      finalFiltersList &&
      finalFiltersList.length >= MAX_SAVE_FILTERS_LIMIT &&
      !preloadParticipantFilter
    ) {
      if (showDeleteMultipleFilterModal === false) {
        setShowDeleteMultipleFilterModal(true);
      }
    } else {
      handleSetParticipantFilterModal();
    }
    const values = getValues();

    const filters: {
      fieldName: keyof ParticipantsSearchFormProps;
      operatorIdKey?: keyof ParticipantsSearchFormProps;
    }[] = [
      {
        fieldName: ParticipantsSearchFormKey.participantFirstName,
        operatorIdKey: ParticipantsSearchFormKey.participantFirstNameFilter,
      },
      {
        fieldName: ParticipantsSearchFormKey.participantLastName,
        operatorIdKey: ParticipantsSearchFormKey.participantLastNameFilter,
      },
      {
        fieldName: ParticipantsSearchFormKey.keyNumber,
        operatorIdKey: ParticipantsSearchFormKey.keyNumberFilter,
      },
      {
        fieldName: ParticipantsSearchFormKey.participantEmail,
        operatorIdKey: ParticipantsSearchFormKey.participantEmailFilter,
      },

      {
        fieldName: ParticipantsSearchFormKey.clientName,
        operatorIdKey: ParticipantsSearchFormKey.clientNameFilter,
      },
      {
        fieldName: ParticipantsSearchFormKey.participantStatus,
      },
      {
        fieldName: ParticipantsSearchFormKey.mobileISOCode,
      },
      {
        fieldName: ParticipantsSearchFormKey.mobileCountryCode,
      },
      {
        fieldName: ParticipantsSearchFormKey.participantMobileNumber,
      },
      {
        fieldName: ParticipantsSearchFormKey.coachType,
      },
      {
        fieldName: ParticipantsSearchFormKey.coachIds,
      },
    ];

    const filteredData: FilterProps[] = filters
      .filter(({ fieldName }) => {
        const filterValue = values[fieldName];
        return Array.isArray(filterValue)
          ? isNotEmpty(filterValue)
          : isNotEmpty(filterValue.trim());
      })
      .map(({ fieldName, operatorIdKey }) => ({
        fieldName: fieldName as string,
        operatorId: operatorIdKey ? (values[operatorIdKey] as string) : "",
        savedSearchFilterDataType: 0,
        fieldValue: Array.isArray(values[fieldName])
          ? (values[fieldName] as string[]).join("|")
          : trimValue(values[fieldName] as string),
      }));

    if (preloadParticipantFilterValues?.id) {
      setPreloadParticipantFilterValues({
        id: preloadParticipantFilterValues.id,
        name: preloadParticipantFilterValues.name,
        filters: filteredData,
      });
    } else {
      setParticipantFiltersData(filteredData);
    }
  };

  const handleDeleteActionClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    item: MenuListParams,
  ) => {
    event.stopPropagation();
    if (preloadParticipantFilterValues.name === item.name) {
      removeSavedFilters();
    }

    setFilterToDeleteParticipant({ id: item?.id, name: item?.name });
    if (showDeleteFilterModal === false) {
      setShowDeleteFilterModal(true);
    }
  };

  const handleDeleteFilterModalClose = () => {
    if (showDeleteFilterModal) {
      setShowDeleteFilterModal(false);
    }
  };

  const handleMultipleDeleteModalClose = () => {
    if (showDeleteMultipleFilterModal) {
      setShowDeleteMultipleFilterModal(false);
    }
  };

  const handlePrimaryButtonClick = () => {
    setShowDeleteFilterModal(false);
  };

  const handleMultipleDeletePrimaryButtonClick = () => {
    setShowDeleteMultipleFilterModal(false);
  };

  const handleSavedFilter = () => {
    if (showParticipantSavedFilter === false) {
      setShowParticipantSavedFilter(true);
    }
  };

  const closeSaveFilterModal = () => {
    setIsOpenParticipantSaveFilterModal(false);
  };

  const handleClose = () => {
    if (showParticipantSavedFilter === true) {
      setShowParticipantSavedFilter(false);
    }
  };

  const handleFilterRowClick = (
    item: MenuListParams | SaveSearchFilterParams,
  ) => {
    setPreloadParticipantFilter(true);
    const itemFilterParticipant = item as SaveSearchFilterParams;
    const mappedValues = mapFiltersToFormValues(
      itemFilterParticipant.filters,
      {
        ...participantsSearchDefaultValues,
        searchType: getValues().searchType,
      },
      ParticipantsSearchFormKey,
    );
    setPreloadParticipantFilterValues({
      id: itemFilterParticipant.id ?? "",
      name: itemFilterParticipant.name ?? "",
      filters: mappedValues,
    });
    reset(mappedValues);
    if (showParticipantSavedFilter === true) {
      setShowParticipantSavedFilter(false);
    }
  };

  const refetchApi = isSearchTypeMyCaseLoads
    ? refetchListOfFiltersMyCaseLoads
    : refetchListOfFiltersAllParticipants;

  const onSaveAsNewFilterClick = () => {
    setParticipantFiltersData([...preloadParticipantFilterValues.filters]);
    if (finalFiltersList && finalFiltersList.length >= MAX_SAVE_FILTERS_LIMIT) {
      setShowDeleteMultipleFilterModal(true);
      setIsMaxFilterReached(true);
    } else {
      setPreloadParticipantFilter(false);
    }
  };

  const callbackAction = () => {
    if (isMaxFilterReached) {
      setPreloadParticipantFilter(false);
    }
    setIsOpenParticipantSaveFilterModal(true);
  };

  const renderFilterSubHeader = () => {
    return (
      <SearchSaveFiltersSubHeader
        onSavedFilterClick={handleSavedFilter}
        onResetAllClick={() => resetFormValueBySearchType(false)}
        anchorRef={anchorRef}
        isSavedFilterDisabled={isEmpty(finalFiltersList)}
      />
    );
  };

  return (
    <ParticipantsSearchWrapper data-testid="participants-search-wrapper">
      <AccordionHeader
        title={
          <AccordionHeaderText
            label={commonLabels.search}
            data-testid="participants-search-accordion-header"
          />
        }
      >
        {renderFilterSubHeader()}
        <ParticipantsSearchForm
          onMyCaseLoadsRadioToggle={() => resetFormValueBySearchType(true)}
          handleSaveFilter={handleParticipantSaveFilterModal}
          coachTypeData={convertCoachDataToDropdown(coachAssignmentData ?? [])}
          coachesList={coachesList}
          makeMobileFieldFocussed={makeMobileFieldFocussed}
        />
      </AccordionHeader>
      {isOpenParticipantSaveFilterModal && !preloadParticipantFilter && (
        <SaveFilterModal
          showModal={isOpenParticipantSaveFilterModal}
          handleModalClose={closeSaveFilterModal}
          pageId={pageId}
          savedSearchPageName={selectedSavedSearchPageName}
          filters={participantFiltersData}
          listOfFilters={finalFiltersList}
          refetchListOfFilters={refetchApi}
        />
      )}

      {preloadParticipantFilter && isOpenParticipantSaveFilterModal && (
        <OverwriteFilterModal
          showModal={
            isOpenParticipantSaveFilterModal && preloadParticipantFilter
          }
          handleModalClose={closeSaveFilterModal}
          handleSaveNewFilterClick={onSaveAsNewFilterClick}
          overrideFilterValues={preloadParticipantFilterValues}
          refetchListOfFilters={refetchApi}
        />
      )}

      {showParticipantSavedFilter &&
        finalFiltersList &&
        (isMobile ? (
          <CommonListModal
            title={commonLabels.savedFilter}
            open={showParticipantSavedFilter}
            onIconButtonClick={handleDeleteActionClick}
            onFilterClick={handleFilterRowClick}
            buttonIcon={<DeleteIcon />}
            listItems={finalFiltersList}
            closeHandler={handleClose}
            secondaryButtonTitle={commonLabels.close}
          />
        ) : (
          <MuiMenuList
            menuArray={finalFiltersList}
            refValue={anchorRef.current}
            open={showParticipantSavedFilter}
            onClose={handleClose}
            onRowClick={handleFilterRowClick}
            onActionBtnClick={handleDeleteActionClick}
          />
        ))}
      {showDeleteFilterModal && (
        <DeleteFilterModal
          showModal={showDeleteFilterModal}
          handleModalClose={handleDeleteFilterModalClose}
          handlePrimaryButtonClick={handlePrimaryButtonClick}
          filterToDelete={filterToDeleteParticipant}
          refetchListOfFilters={refetchApi}
        />
      )}

      {showDeleteMultipleFilterModal && (
        <DeleteMultipleFilterModal
          showModal={showDeleteMultipleFilterModal}
          handleModalClose={handleMultipleDeleteModalClose}
          handlePrimaryButtonClick={handleMultipleDeletePrimaryButtonClick}
          listOfFilters={finalFiltersList}
          refetchListOfFilters={refetchApi}
          callbackAction={callbackAction}
        />
      )}
    </ParticipantsSearchWrapper>
  );
};
